import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { IParentBroker } from '../../shared/atom/inputs/brokerRecordTypes'

interface CustomBrokersModalProps {
  open: boolean
  onClose: () => void
  dataBrokerOptions: IParentBroker[]
  configuredBrokers: Record<string, string[]>
  onChange: (newConfigured: Record<string, string[]>) => void
}

export const CustomBrokersModal: React.FC<CustomBrokersModalProps> = ({
  open,
  onClose,
  dataBrokerOptions,
  configuredBrokers,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [draft, setDraft] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(configuredBrokers || {})))
    }
  }, [open, configuredBrokers])

  const grouped = (dataBrokerOptions || []).reduce((acc, broker) => {
    const cat = broker.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(broker)
    return acc
  }, {} as Record<string, IParentBroker[]>)

  const filteredGrouped = Object.entries(grouped).reduce((acc, [category, brokers]) => {
    if (!searchQuery.trim()) {
      acc[category] = brokers
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = brokers.filter(
        (b) =>
          b.display_name.toLowerCase().includes(query) ||
          b.name.toLowerCase().includes(query)
      )
      if (filtered.length > 0) {
        acc[category] = filtered
      }
    }
    return acc
  }, {} as Record<string, IParentBroker[]>)

  const handleSelectAll = (category: string, brokers: IParentBroker[]) => {
    const allNames = brokers.map((b) => b.name)
    const currentSelected = draft?.[category] || []
    const allSelected = allNames.every((name) => currentSelected.includes(name))

    const newDraft = { ...draft }
    if (allSelected) {
      newDraft[category] = []
    } else {
      newDraft[category] = [...allNames]
    }
    setDraft(newDraft)
  }

  const handleDiscard = () => {
    setDraft(JSON.parse(JSON.stringify(configuredBrokers || {})))
    setSearchQuery('')
    onClose()
  }

  const handleDone = () => {
    setSearchQuery('')
    onChange(draft)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleDiscard}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { maxHeight: '60vh' },
      }}
    >
      <DialogTitle sx={{ px: 4 }}>
        <Typography sx={{ color: '#3A155A', fontSize: '1.5rem', fontWeight: 700 }}>
          Select Custom Brokers
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {/* Search input */}
        <Box sx={{ px: 3, py: 2, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search brokers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#9e9e9e"/>
                  </svg>
                </InputAdornment>
              ),
              ...(searchQuery && {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      onClick={() => setSearchQuery('')}
                      sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'grey.400', '&:hover': { color: 'grey.700' } }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                      </svg>
                    </Box>
                  </InputAdornment>
                ),
              }),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&.Mui-focused fieldset': { borderColor: '#3A155A' },
              },
            }}
          />
        </Box>

        {/* Broker list */}
        <Box>
          {Object.keys(filteredGrouped).length === 0 ? (
            <Box sx={{ px: 4, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                No brokers found
              </Typography>
            </Box>
          ) : (
            Object.entries(filteredGrouped).map(([category, brokers], idx) => {
              const currentSelected = draft?.[category] || []
              const fullCategoryBrokers = grouped[category] || []
              const allSelected =
                fullCategoryBrokers.length > 0 &&
                fullCategoryBrokers.every((b) => currentSelected.includes(b.name))

              return (
                <Box key={category}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pl: 4,
                      pr: 2,
                      py: 3,
                      bgcolor: '#f4f6f8',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      borderTop: idx !== 0 ? '1px solid' : 'none',
                      borderTopColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#3A155A',
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {category.replace(/_/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.75rem', color: 'grey.500' }}
                      >
                        Select All
                      </Typography>
                      <Switch
                        size="small"
                        checked={allSelected}
                        onChange={() => handleSelectAll(category, fullCategoryBrokers)}
                      />
                    </Box>
                  </Box>
                  {brokers.map((broker) => {
                    const isChecked = !!draft?.[category]?.includes(
                      broker.name
                    )
                    return (
                      <Box
                        key={broker.name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          pl: 4,
                          pr: 2,
                          py: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {broker.display_name}
                        </Typography>
                        <Switch
                          size="small"
                          checked={isChecked}
                          onChange={(e) => {
                            const checked = e.target.checked
                            const newDraft = { ...draft }
                            if (!newDraft[category]) newDraft[category] = []

                            if (checked) {
                              newDraft[category] = [
                                ...newDraft[category],
                                broker.name,
                              ]
                            } else {
                              newDraft[category] = newDraft[category].filter(
                                (id) => id !== broker.name
                              )
                            }
                            setDraft(newDraft)
                          }}
                        />
                      </Box>
                    )
                  })}
                </Box>
              )
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 3 }}>
        <Button onClick={handleDone} variant="contained" color="primary" sx={{ minWidth: 120 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}